"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResultPromise = exports.Result = void 0;
const error_1 = require("./error");
const promiseExecutor_1 = require("./promiseExecutor");
/** Represent operations that may fail */
var Result;
(function (Result) {
    // Anys here help with inference when a function returns a result.ok and a result.err at different stages
    function ok(value) {
        return new Ok(value);
    }
    Result.ok = ok;
    function err(value) {
        return new Err(value);
    }
    Result.err = err;
    /** An 'async' version of `ok`; returns an AsyncResult containing the given value */
    function asyncOk(value) {
        if (value instanceof Promise)
            return ResultPromise.from(value.then((v) => Result.ok(v)));
        return ResultPromise.from(Promise.resolve(Result.ok(value)));
    }
    Result.asyncOk = asyncOk;
    /** An 'async' version of `err`; returns an AsyncResult containing the given value */
    function asyncErr(value) {
        if (value instanceof Promise)
            return ResultPromise.from(value.then((v) => Result.err(v)));
        return ResultPromise.from(Promise.resolve(Result.err(value)));
    }
    Result.asyncErr = asyncErr;
    function all(results) {
        const values = [];
        const errors = [];
        for (const result of results) {
            if (result.isOk())
                values.push(result.value);
            else
                errors.push(result.error);
        }
        if (errors.length > 0)
            return Result.err(errors);
        else
            return Result.ok(values);
    }
    Result.all = all;
})(Result = exports.Result || (exports.Result = {}));
class ResultPromise extends Promise {
    /**
     * Create an `AsyncResult` from a `Promise` containing a `Result`
     */
    static from(promise) {
        return promiseExecutor_1.extendedPromise(ResultPromise, promise);
    }
    constructor(executor) {
        super(executor);
    }
    /**
     * Transforms the value inside an `Ok`, or returns the `Err` if called
     * on an `Err`.
     *
     * The function provided can also return a `Promise`, and that promise will
     * be resolved as required
     */
    map(fn) {
        return this.promiseChain((result) => __awaiter(this, void 0, void 0, function* () { return result.isOk() ? Result.ok(yield fn(result.value)) : Result.err(result.error); }));
    }
    /**
     * Transforms the value inside an `Err`, or returns the `Ok` if called
     * on an `Ok`.
     *
     * The function provided can also return a `Promise`, and that promise will
     * be resolved as required
     */
    mapErr(fn) {
        return this.promiseChain((result) => __awaiter(this, void 0, void 0, function* () { return result.isErr() ? Result.err(yield fn(result.error)) : Result.ok(result.value); }));
    }
    /**
     * Calls the predicate with the value in the `Ok`. Returns the `Ok` if the
     * predicate is true, or an `Err` with the value in `onErr` otherwise. If
     * `filter` is called on an `Err` it will return that `Err`
     *
     * The return value of the function, and the value provided, can also be
     * `Promise`s, and thesepromise will be resolved as required
     */
    filter(predicate, onErr) {
        return this.promiseChain((result) => __awaiter(this, void 0, void 0, function* () {
            return result.isErr()
                ? Result.err(result.error)
                : result.isOk() && (yield predicate(result.value))
                    ? Result.ok(result.value)
                    : Result.err(yield onErr);
        }));
    }
    /**
     * The monadic `bind`.
     *
     * Allows the chaining functions that return `Result`s.
     * Returns the result of the function, or the `Err` if called on an `Err`
     *
     * The function provided can also return a `Promise`, and that promise will
     * be resolved as required
     */
    chain(fn) {
        return this.promiseChain((result) => __awaiter(this, void 0, void 0, function* () { return (result.isOk() ? fn(result.value) : Result.err(result.error)); }));
    }
    /**
     * Either resolve to the value of the `Ok` or reject the promise if called
     * on an `Err`.
     */
    orThrow(prefix) {
        return this.then((result) => result.orThrow(prefix));
    }
    caseOf(patterns) {
        return this.then((result) => result.caseOf(patterns));
    }
    /**
     * Either resolve to the value of the `Ok`, or the value given
     *
     * The value provided can also be a `Promise`, and that promise will
     * be resolved as required
     */
    withDefault(value) {
        return __awaiter(this, void 0, void 0, function* () {
            const defaultValue = yield value;
            return this.then((result) => result.withDefault(defaultValue));
        });
    }
    promiseChain(fn) {
        return new ResultPromise((resolve, reject) => {
            this.then((result) => resolve(fn(result)), reject);
        });
    }
    toUnion() {
        return this.then((result) => result.toUnion());
    }
}
exports.ResultPromise = ResultPromise;
class Ok {
    constructor(value) {
        this.value = value;
    }
    isOk() {
        return true;
    }
    isErr() {
        return false;
    }
    /** Transforms the value inside an `Ok`, or returns the `Err` if called on an `Err` */
    map(fn) {
        return Result.ok(fn(this.value));
    }
    /** Transforms the value inside an `Err`, or returns the `Ok` if called on an `Ok` */
    mapErr(_) {
        return Result.ok(this.value);
    }
    /**
     * Calls the predicate with the value in the `Ok`. Returns the `Ok` if the
     * predicate is true, or an `Err` with the value in `onErr` otherwise. If
     * `filter` is called on an `Err` it will return that `Err`
     */
    filter(predicate, onErr) {
        return predicate(this.value) ? Result.ok(this.value) : Result.err(onErr);
    }
    /**
     * The monadic `bind`.
     *
     * Allows the chaining functions that return `Result`s.
     * Returns the result of the function, or the `Err` if called on an `Err`
     */
    chain(fn) {
        return fn(this.value);
    }
    orThrow(_) {
        return this.value;
    }
    caseOf({ ok }) {
        return ok(this.value);
    }
    /** Either return the value of the `Ok`, or the value given */
    withDefault(_) {
        return this.value;
    }
    toString() {
        return `Ok(${JSON.stringify(this.value, null, 2)})`;
    }
    toUnion() {
        return this.value;
    }
}
class Err {
    constructor(error) {
        this.error = error;
    }
    isOk() {
        return false;
    }
    isErr() {
        return true;
    }
    /** Transforms the value inside an `Ok`, or returns the `Err` if called on an `Err` */
    map(_) {
        return Result.err(this.error);
    }
    /** Transforms the value inside an `Err`, or returns the `Ok` if called on an `ok` */
    mapErr(fn) {
        return Result.err(fn(this.error));
    }
    /**
     * Calls the predicate with the value in the `Ok`. Returns the `Ok` if the predicate is true,
     * or an `Err` with the value in `isErr` otherwise. If `filter` is called on an `Err` it will
     * return that `Err`
     */
    filter(_, __) {
        return Result.err(this.error);
    }
    /**
     * The monadic `bind`.
     *
     * Allows the chaining functions that return `Result`s.
     * Returns the result of the function, or the `Err` if called on an `Err`
     */
    chain(_) {
        return Result.err(this.error);
    }
    /**
     * Either return the value of the `Ok` or throw an error if called on an `Err`
     * This is here mainly for testing. Consider using an if statement with `.isOk`
     * or `.isErr` in other situations
     */
    orThrow(prefix) {
        const error = error_1.prefixError(prefix, error_1.ensureError(this.error));
        throw error;
    }
    caseOf({ err }) {
        return err(this.error);
    }
    /** Either return the value of the `Ok`, or the value given */
    withDefault(value) {
        return value;
    }
    toString() {
        return `Err(${JSON.stringify(this.error, null, 2)})`;
    }
    toUnion() {
        return this.error;
    }
}
//# sourceMappingURL=result.js.map